import React, { useCallback, useEffect, useState } from "react";
import {
  Bell, BellSlash, CheckCircle, WarningCircle, XCircle, DeviceMobile,
  Desktop, Browser, ArrowsClockwise, PaperPlaneRight, ShieldCheck, ShieldWarning,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  isPushSupported,
  pushPermission,
  getSubscription,
  enablePush,
  disablePush,
  sendTestPush,
  resubscribe,
  ensureFreshSubscription,
} from "../lib/push";

/**
 * Push diagnostics + controls panel.
 *
 * Renders a complete checklist so the user can verify whether push works
 * on this device and across their devices:
 *   1. Browser support (Service Worker, PushManager, Notification API)
 *   2. Notification permission (granted/denied/default)
 *   3. Service worker registration state
 *   4. Local subscription (this device)
 *   5. Devices subscribed to this account (from backend)
 *   6. Server-side VAPID configuration (from backend)
 *   7. Send-test button with a clear result indicator
 */
export default function PushSettings() {
  const supported = isPushSupported();
  const [perm, setPerm] = useState(supported ? pushPermission() : "unsupported");
  const [localSub, setLocalSub] = useState(false);
  const [swState, setSwState] = useState("checking"); // "active" | "missing" | "checking"
  const [diag, setDiag] = useState(null); // {vapid_configured, device_count, devices[]}
  const [busy, setBusy] = useState(false);
  const [lastTest, setLastTest] = useState(null); // { ts, sent, ok }

  const refresh = useCallback(async () => {
    if (supported) {
      setPerm(pushPermission());
      const s = await getSubscription();
      setLocalSub(!!s);
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        setSwState(reg && (reg.active || reg.installing || reg.waiting) ? "active" : "missing");
      } catch { setSwState("missing"); }
    }
    try {
      const { data } = await api.get("/push/diagnostics");
      setDiag(data);
    } catch {
      setDiag({ vapid_configured: false, device_count: 0, devices: [] });
    }
  }, [supported]);

  // First-mount: heal any stale VAPID-bound subscription transparently,
  // then load the diagnostic state.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await ensureFreshSubscription();
        if (alive && r?.changed) {
          toast.success("Notificaciones re-sincronizadas con el servidor");
        }
      } catch { /* ignore */ }
      if (alive) refresh();
    })();
    return () => { alive = false; };
  }, [refresh]);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notificaciones activadas en este dispositivo");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "No se pudo activar");
    } finally { setBusy(false); }
  };
  const onDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Notificaciones desactivadas en este dispositivo");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally { setBusy(false); }
  };
  const onTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      const sent = r?.sent ?? 0;
      const failed = r?.failed ?? 0;
      const statuses = r?.statuses || [];
      setLastTest({ ts: Date.now(), sent, failed, ok: sent > 0, statuses });
      if (sent > 0) toast.success(`Notificación enviada a ${sent} dispositivo${sent > 1 ? "s" : ""}`);
      else toast.error("No se entregó a ningún dispositivo (revisa el detalle abajo)");
      await refresh();
    } catch (e) {
      setLastTest({ ts: Date.now(), sent: 0, failed: 1, ok: false, statuses: [], errorMessage: e?.message });
      toast.error(e?.message || "Error al enviar prueba");
    } finally { setBusy(false); }
  };

  const onResubscribe = async () => {
    setBusy(true);
    try {
      await resubscribe();
      toast.success("Re-suscrito. Probando envío…");
      await refresh();
      // Auto-test after re-subscribing so the user sees instant feedback
      const r = await sendTestPush();
      const sent = r?.sent ?? 0;
      setLastTest({ ts: Date.now(), sent, failed: r?.failed ?? 0, ok: sent > 0, statuses: r?.statuses || [] });
      if (sent > 0) toast.success("¡Funciona! Notificación enviada");
      else toast.error("Re-suscrito pero el envío sigue fallando");
    } catch (e) {
      toast.error(e?.message || "Error al re-suscribir");
    } finally { setBusy(false); }
  };

  const isStandalone = typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);
  const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent);

  return (
    <div className="bento p-6 space-y-5" data-testid="push-settings">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-forest text-bg flex items-center justify-center flex-shrink-0">
            <Bell size={18} weight="duotone" />
          </div>
          <div>
            <div className="font-display text-lg font-bold leading-tight">Notificaciones push</div>
            <p className="text-sm text-inkMuted mt-1 max-w-prose">
              Alertas de chat, créditos y transferencias. Diagnostica tu configuración y prueba el envío.
            </p>
          </div>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          className="h-8 px-2.5 border border-hairline text-[11px] font-mono uppercase tracking-widest hover:bg-bg disabled:opacity-50 flex items-center gap-1.5"
          data-testid="push-refresh"
          title="Re-verificar estado"
        >
          <ArrowsClockwise size={12} weight="bold" /> Refrescar
        </button>
      </div>

      {/* Server-side VAPID warning — only show if not configured */}
      {diag && !diag.vapid_configured && (
        <div className="p-3 bg-danger/10 border border-danger/30 text-sm flex items-start gap-2" data-testid="push-server-vapid-missing">
          <ShieldWarning size={16} weight="duotone" className="text-danger flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">El servidor no puede enviar notificaciones</div>
            <div className="text-xs text-inkMuted mt-0.5">
              Faltan las claves VAPID en producción. Contacta al equipo técnico para configurarlas.
            </div>
          </div>
        </div>
      )}

      {/* Checklist */}
      <div className="space-y-2.5" data-testid="push-checklist">
        <Check
          ok={supported}
          label="Tu navegador soporta notificaciones push"
          hint={!supported ? (isIOS && !isStandalone
            ? "En iPhone, primero instala la app desde Safari (Compartir → Agregar a Inicio)."
            : "Usa Chrome, Edge o Firefox modernos.") : null}
        />
        <Check
          ok={swState === "active"}
          warning={swState === "checking"}
          label="Service Worker registrado"
          hint={swState === "missing" ? "Recarga la página o reinstala la app." : null}
        />
        <Check
          ok={perm === "granted"}
          warning={perm === "default"}
          fail={perm === "denied"}
          label={
            perm === "granted" ? "Permiso del navegador concedido"
            : perm === "denied" ? "Permiso bloqueado"
            : perm === "default" ? "Permiso no solicitado todavía"
            : "Permiso no disponible"
          }
          hint={perm === "denied" ? "Habilita las notificaciones desde la configuración del navegador para este sitio." : null}
        />
        <Check
          ok={localSub}
          warning={!localSub && perm !== "denied"}
          label={localSub ? "Suscripción activa en este dispositivo" : "Sin suscripción en este dispositivo"}
        />
        <Check
          ok={!!(diag && diag.vapid_configured)}
          label="Servidor con VAPID configurado"
          fail={diag !== null && !diag.vapid_configured}
        />
      </div>

      {/* Devices */}
      {diag && diag.devices && (
        <div className="border-t border-hairline pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="label-mono">Dispositivos suscritos a tu cuenta</div>
            <div className="font-mono text-xs text-inkMuted">{diag.device_count}</div>
          </div>
          {diag.device_count === 0 ? (
            <div className="text-sm text-inkMuted" data-testid="push-devices-empty">
              Ninguno todavía. Activa las notificaciones más abajo y se registrará este dispositivo.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="push-devices-list">
              {diag.devices.map((d, i) => (
                <li key={d.endpoint || `${d.service}-${i}`} className="flex items-center gap-2.5 text-xs py-1.5 px-2.5 bg-bg border border-hairline">
                  <DeviceIcon service={d.service} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{d.service}</div>
                    <div className="font-mono text-[10px] text-inkMuted truncate">{d.user_agent || d.endpoint_host}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Actions */}
      {!supported && isIOS && !isStandalone && (
        <div className="p-3 bg-warn/10 border border-warn/40 text-sm flex items-start gap-2" data-testid="push-unsupported">
          <WarningCircle size={16} weight="duotone" className="text-warn flex-shrink-0 mt-0.5" />
          <div>
            En iPhone, abre el menú <b>Compartir</b> en Safari y elige <b>Agregar a Inicio</b>. Luego vuelve aquí desde el ícono instalado para activarlas.
          </div>
        </div>
      )}

      {supported && (
        <div className="border-t border-hairline pt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2.5">
            {!localSub ? (
              <button
                onClick={onEnable}
                disabled={busy || perm === "denied"}
                data-testid="push-enable"
                className="h-10 px-4 bg-forest text-bg text-sm font-semibold flex items-center gap-2 hover:bg-forest-hover disabled:opacity-50"
              >
                <Bell size={14} weight="bold" /> {busy ? "Activando…" : "Activar en este dispositivo"}
              </button>
            ) : (
              <button
                onClick={onDisable}
                disabled={busy}
                data-testid="push-disable"
                className="h-10 px-4 border border-hairline text-sm font-semibold hover:bg-danger hover:text-bg hover:border-danger flex items-center gap-2 disabled:opacity-50"
              >
                <BellSlash size={14} weight="bold" /> Desactivar este dispositivo
              </button>
            )}
            <button
              onClick={onTest}
              disabled={busy || !localSub || !(diag && diag.vapid_configured)}
              data-testid="push-test"
              className="h-10 px-4 border border-hairline text-sm font-semibold hover:bg-bg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!localSub ? "Activa las notificaciones primero" : !(diag && diag.vapid_configured) ? "El servidor no tiene VAPID configurado" : ""}
            >
              <PaperPlaneRight size={14} weight="bold" /> Enviar notificación de prueba
            </button>
          </div>

          {/* Re-subscribe — full-width on mobile so it's never lost off-screen */}
          {localSub && (
            <button
              onClick={onResubscribe}
              disabled={busy}
              data-testid="push-resubscribe"
              className="w-full h-11 bg-burnt text-bg text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
              title="Regenera la suscripción con las claves actuales del servidor. Útil si no te llegan las notificaciones."
            >
              <ArrowClockwise size={16} weight="bold" /> {busy ? "Re-suscribiendo…" : "Re-suscribir este dispositivo"}
            </button>
          )}

          {lastTest && (
            <div
              data-testid="push-test-result"
              className={`text-xs p-3 space-y-2 ${lastTest.ok ? "bg-success/10 border border-success/30 text-success" : "bg-danger/10 border border-danger/30 text-danger"}`}
            >
              <div className="flex items-start gap-2">
                {lastTest.ok
                  ? <CheckCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                  : <XCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />}
                <div>
                  {lastTest.ok
                    ? <>Enviada a <b>{lastTest.sent}</b> dispositivo{lastTest.sent > 1 ? "s" : ""}. Si no la viste, revisa que las notificaciones del navegador estén habilitadas en tu sistema operativo (Configuración → Apps → Chrome → Notificaciones).</>
                    : <>No se entregó a ningún dispositivo.</>}
                </div>
              </div>

              {/* Per-device status */}
              {lastTest.statuses && lastTest.statuses.length > 0 && (
                <ul className="space-y-1 pl-5 border-l border-current/20" data-testid="push-test-statuses">
                  {lastTest.statuses.map((s, i) => (
                    <li key={`${s.host || "dev"}-${i}`} className="font-mono text-[10px] leading-relaxed">
                      <b>HTTP {s.status || "—"}</b> · {s.host}
                      {s.error && <div className="opacity-70">{s.error}</div>}
                    </li>
                  ))}
                </ul>
              )}

              {/* Smart hint based on common errors */}
              {!lastTest.ok && lastTest.statuses && lastTest.statuses.some((s) => s.status === 403 || /VapidPkHashMismatch|mismatch/i.test(s.error || "")) && (
                <div className="text-[11px] pt-1 border-t border-current/20">
                  <b>Causa probable:</b> tu suscripción quedó vinculada a una clave antigua del servidor.
                  Toca <b>Re-suscribir</b> para regenerarla.
                </div>
              )}
              {!lastTest.ok && lastTest.statuses && lastTest.statuses.some((s) => s.status === 410 || s.status === 404) && (
                <div className="text-[11px] pt-1 border-t border-current/20">
                  <b>Causa:</b> la suscripción de este dispositivo caducó. Toca <b>Activar</b> de nuevo.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Check({ ok, warning, fail, label, hint }) {
  let Icon = CheckCircle;
  let color = "text-success";
  if (fail) { Icon = XCircle; color = "text-danger"; }
  else if (warning && !ok) { Icon = WarningCircle; color = "text-warn"; }
  else if (!ok) { Icon = WarningCircle; color = "text-inkMuted"; }
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon size={16} weight="fill" className={`${color} flex-shrink-0 mt-0.5`} />
      <div className="flex-1">
        <div>{label}</div>
        {hint && <div className="text-xs text-inkMuted mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function DeviceIcon({ service }) {
  const cls = "text-inkMuted flex-shrink-0";
  if (/Android|Chrome/i.test(service)) return <DeviceMobile size={16} weight="duotone" className={cls} />;
  if (/iOS|Safari/i.test(service)) return <DeviceMobile size={16} weight="duotone" className={cls} />;
  if (/Windows/i.test(service)) return <Desktop size={16} weight="duotone" className={cls} />;
  if (/Firefox/i.test(service)) return <Browser size={16} weight="duotone" className={cls} />;
  return <ShieldCheck size={16} weight="duotone" className={cls} />;
}
